import React from 'react';
import Icon from 'components/AppIcon';

const ProductSlide = () => {
  const coreModules = [
    {
      icon: 'Search',
      title: 'Client Portal',
      features: [
        'Lawyer matching and selection',
        'Case timeline and progress tracking',
        'Secure document access',
        'Communication center',
        'Invoice and payment tracking'
      ],
      color: 'primary'
    },
    {
      icon: 'Scale',
      title: 'Lawyer Dashboard',
      features: [
        'Complete case management',
        'Client relationship management',
        'Document management with versioning',
        'Video consultation platform',
        'Financial tracking and invoicing'
      ],
      color: 'secondary'
    },
    {
      icon: 'BarChart3',
      title: 'Partner Portal',
      features: [
        'Practice analytics and insights',
        'Partner performance metrics',
        'Administrative controls',
        'Strategic planning tools',
        'Financial overview'
      ],
      color: 'success'
    }
  ];

  const technicalFeatures = [
    {
      icon: 'Shield',
      title: 'Security & Compliance',
      description: 'Bank-level encryption, secure communications, data privacy compliance'
    },
    {
      icon: 'Smartphone',
      title: 'Mobile Responsive',
      description: 'Fully responsive design optimized for mobile devices'
    },
    {
      icon: 'Cloud',
      title: 'Cloud-Based',
      description: 'Scalable cloud infrastructure with 99.9% uptime guarantee'
    },
    {
      icon: 'Globe',
      title: 'Multi-Language',
      description: 'Support for English and Swahili languages'
    }
  ];

  const integrations = [
    {
      icon: 'CreditCard',
      title: 'Payment Systems',
      description: 'M-Pesa, banking, and international payment gateways'
    },
    {
      icon: 'Building',
      title: 'Court Systems',
      description: 'Integration with e-filing and court management systems'
    },
    {
      icon: 'FileText',
      title: 'Document Systems',
      description: 'Legal document templates and e-signature capabilities'
    },
    {
      icon: 'Mail',
      title: 'Communication',
      description: 'Email, SMS, and push notification systems'
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: 'bg-primary-50 text-primary border-primary-200',
      secondary: 'bg-secondary-50 text-secondary-600 border-secondary-200',
      success: 'bg-success-50 text-success-600 border-success-200'
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold text-text-primary mb-4">
            Product Features
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Comprehensive platform with three integrated portals serving all stakeholders
          </p>
        </div>

        {/* Core Modules */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {coreModules?.map((module, index) => (
            <div key={index} className={`rounded-lg p-6 border-2 ${getColorClasses(module?.color)}`}>
              <div className="flex items-center space-x-3 mb-6">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  module?.color === 'primary' ? 'bg-primary text-white' :
                  module?.color === 'secondary'? 'bg-secondary-600 text-white' : 'bg-success-600 text-white'
                }`}>
                  <Icon name={module?.icon} size={24} />
                </div>
                <h2 className="text-xl font-heading font-bold">{module?.title}</h2>
              </div>
              
              <ul className="space-y-3">
                {module?.features?.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-current mt-1 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Technical Features */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Technical Capabilities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technicalFeatures?.map((feature, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border text-center">
                <div className="w-12 h-12 bg-accent-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name={feature?.icon} size={24} className="text-accent" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
                  {feature?.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {feature?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Integrations */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Key Integrations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrations?.map((integration, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={integration?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
                  {integration?.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {integration?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Stats */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-8">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-6 text-center">
            Platform Ready Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-primary mb-2">15+</div>
              <p className="text-text-secondary">Core modules completed</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-primary mb-2">100%</div>
              <p className="text-text-secondary">Mobile responsive</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-primary mb-2">99.9%</div>
              <p className="text-text-secondary">Uptime guarantee</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-heading font-bold text-primary mb-2">Bank-Level</div>
              <p className="text-text-secondary">Security standards</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSlide;